import {
  Channels,
  dispatch,
  Events,
  Sources,
} from '@npm-client-frontends/client-frontends-custom-events'
import { CognitoGroups } from '@npm-client-frontends/client-frontends-shared-types'
import React, { useState, useEffect } from 'react'
import {
  Authenticator,
  ThemeProvider,
  useAuthenticator,
  View,
} from '@aws-amplify/ui-react'
import {
  getCurrentUser,
  signIn,
  fetchAuthSession,
  SignInInput,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth'
import formFields from './layout/formFields'
import theme from './layout/theme'
import { Header, SignInHeader, ResetPasswordHeader } from './components/auth'
import '@aws-amplify/ui-react/styles.css'

const components = {
  Header,
  SignIn: {
    Header: SignInHeader,
  },
  ResetPassword: {
    Header: ResetPasswordHeader,
  },
}

const checkForApiUser = async () => {
  const session = await fetchAuthSession()
  const groups = (session.tokens?.accessToken?.payload?.['cognito:groups'] ??
    []) as string[]
  return groups.includes(CognitoGroups.CustomerAPI)
}

const services = {
  async handleSignIn(input: SignInInput): Promise<SignInOutput> {
    const { isSignedIn, nextStep } = await signIn({
      username: input.username,
      password: input.password,
    })

    if (isSignedIn) {
      const isApiUser = await checkForApiUser()
      if (isApiUser) {
        await signOut()
        throw new Error(
          'These user credentials are designated for API access only. Please sign in again using your regular NPM user account.',
        )
      }
    }

    return { isSignedIn, nextStep }
  },
}

export const LoginPage = () => {
  const [shouldRender, setShouldRender] = useState(false)

  window.addEventListener('popstate', () => {
    setShouldRender(window.location.pathname.startsWith('/login'))
  })

  const { route } = useAuthenticator()

  async function checkUser() {
    try {
      await getCurrentUser()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.info('User unauthenticated: ', error)
    }
  }

  useEffect(() => {
    void checkUser()
    setShouldRender(window.location.pathname.startsWith('/login'))
  }, [])

  useEffect(() => {
    if (route === 'authenticated') {
      const preLoginRoute = sessionStorage.getItem('preLoginRoute')
      if (preLoginRoute) {
        dispatch(
          Channels.AUTH,
          Sources.LOGIN,
          Events.USER_AUTHENTICATED,
          preLoginRoute,
        )
        sessionStorage.removeItem('preLoginRoute')
      }
    }
  }, [route])

  if (!shouldRender) return null

  return (
    <ThemeProvider theme={theme}>
      <View
        style={{
          height: '100vh',
          width: '100vw',
          backgroundColor: '#323d61',
          paddingBottom: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div data-testid="authenticator">
          <Authenticator
            components={components}
            formFields={formFields}
            services={services}
            hideSignUp
          />
        </div>
      </View>
    </ThemeProvider>
  )
}
